/**
 * Who has a training — TRUS-562 items 14 and 6.
 *
 * Abby (2026-10-29): "being able to view for each training when assigning staff — who
 * is already assigned and have the ability to edit those staff (like I mistakenly
 * added someone who needs to be removed) or I forgot someone."
 *
 * Opened from the Catalogue. Lists everyone the training is assigned to, with status,
 * due date and completion; Assign more hands back to the assign dialog, and Unassign
 * takes it off one person with an optional reason. Unassigning keeps their completion
 * records, and the nightly evaluator leaves them off until someone assigns them again.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { UserMinus, UserPlus } from 'lucide-react'
import { Button, ErrorNote, INPUT, Modal, Pill } from './ui'
import { Assignment, Training, formatDate, isDoneUntilNextDue, listAssignments, statusTone, unassignTraining } from './api'

const STATUS_LABEL: Record<Assignment['status'], string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  pending: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Unassigned',
}

const HOW_LABEL: Record<Assignment['assigned_via'], string> = {
  rule: 'By schedule rule',
  role: 'By role',
  individual: 'By name',
}

const STATUS_ORDER: Record<Assignment['status'], number> = {
  overdue: 0,
  due_soon: 1,
  pending: 2,
  completed: 3,
  cancelled: 4,
}

const TH = 'py-1.5 pr-3 text-left font-medium text-ink-500'
const TD = 'py-1.5 pr-3 align-top'

export function TrainingAssigneesDialog({
  training,
  open,
  onClose,
  onAssignMore,
}: {
  training: Training | null
  open: boolean
  onClose: () => void
  onAssignMore: (t: Training) => void
}) {
  const [rows, setRows] = useState<Assignment[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The row whose Unassign is being confirmed, and the reason typed for it.
  const [confirming, setConfirming] = useState<{ id: number; reason: string; busy: boolean } | null>(null)

  const load = useCallback(async () => {
    if (!training) return
    setRows(null)
    setError(null)
    try {
      const res = await listAssignments({
        training_id: training.id,
        open_only: false,
        include_cancelled: false,
        limit: 500,
      })
      setRows(res.assignments)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load who has this training.')
    }
  }, [training])

  // Reload on every opening, including coming back from Assign more.
  useEffect(() => {
    if (open) {
      setConfirming(null)
      void load()
    }
  }, [open, load])

  const sorted = useMemo(
    () =>
      (rows ?? [])
        .slice()
        .sort(
          (a, b) =>
            STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
            (a.user_name ?? '').localeCompare(b.user_name ?? ''),
        ),
    [rows],
  )

  const counts = useMemo(() => {
    const c = { open: 0, done: 0 }
    for (const a of rows ?? []) {
      if (a.status === 'completed' || isDoneUntilNextDue(a)) c.done += 1
      else c.open += 1
    }
    return c
  }, [rows])

  const doUnassign = async () => {
    if (!confirming) return
    setConfirming({ ...confirming, busy: true })
    try {
      await unassignTraining(confirming.id, confirming.reason)
      setConfirming(null)
      await load()
    } catch (e) {
      setConfirming({ ...confirming, busy: false })
      setError(e instanceof Error ? e.message : 'Could not unassign.')
    }
  }

  return (
    <Modal
      open={open && training !== null}
      wide
      title={training ? `Assigned: ${training.title}` : 'Assigned staff'}
      description="Everyone who has this training, where they stand, and when it is due."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => training && onAssignMore(training)} disabled={!training}>
            <UserPlus className="h-4 w-4" /> Assign more
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px]">
        {error && <ErrorNote message={error} onDismiss={() => setError(null)} />}
        {rows === null ? (
          !error && <p className="text-ink-500">Loading who has this training…</p>
        ) : rows.length === 0 ? (
          <p className="text-ink-500">Nobody has this training assigned yet. Use Assign more to add staff.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Pill>{rows.length} assigned</Pill>
              {counts.open ? <Pill tone="attention">{counts.open} still to complete</Pill> : null}
              {counts.done ? <Pill tone="success">{counts.done} completed</Pill> : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={TH}>Name</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Due</th>
                    <th className={TH}>Completed</th>
                    <th className={TH}>How assigned</th>
                    <th className={TH} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((a) => (
                    <React.Fragment key={a.id}>
                      <tr className="border-t border-v2border-muted">
                        <td className={`${TD} text-ink`}>{a.user_name ?? `Staff #${a.user_id}`}</td>
                        <td className={TD}>
                          {isDoneUntilNextDue(a) ? (
                            <Pill tone="success">Completed · next due {a.due_date ? formatDate(a.due_date) : 'later'}</Pill>
                          ) : (
                            <Pill tone={statusTone(a.status)}>{STATUS_LABEL[a.status]}</Pill>
                          )}
                        </td>
                        <td className={TD}>
                          {a.due_date ? formatDate(a.due_date) : <span className="text-ink-400">No due date</span>}
                        </td>
                        <td className={TD}>
                          {a.completed_date ? formatDate(a.completed_date) : <span className="text-ink-400">—</span>}
                        </td>
                        <td className={`${TD} text-ink-500`}>{HOW_LABEL[a.assigned_via] ?? a.assigned_via}</td>
                        <td className={`${TD} text-right`}>
                          {confirming?.id !== a.id && (
                            <Button
                              variant="ghost"
                              onClick={() => setConfirming({ id: a.id, reason: '', busy: false })}
                              title="Take this training off this person"
                            >
                              <UserMinus className="h-4 w-4" /> Unassign
                            </Button>
                          )}
                        </td>
                      </tr>
                      {confirming?.id === a.id && (
                        <tr>
                          <td colSpan={6} className="pb-3">
                            <div className="rounded-md border border-attention bg-attention-soft p-3 text-attention-ink">
                              <p>
                                Unassign this training from {a.user_name ?? 'this person'}? Their completion records
                                are kept, and they won&apos;t be assigned it again automatically.
                              </p>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <input
                                  className={`${INPUT} min-w-[220px] flex-1`}
                                  placeholder="Reason (optional), e.g. added by mistake"
                                  value={confirming.reason}
                                  onChange={(e) => setConfirming({ ...confirming, reason: e.target.value })}
                                  aria-label="Reason for unassigning"
                                />
                                <Button onClick={() => setConfirming(null)} disabled={confirming.busy}>
                                  Keep
                                </Button>
                                <Button variant="danger" onClick={() => void doUnassign()} disabled={confirming.busy}>
                                  {confirming.busy ? 'Unassigning…' : 'Unassign'}
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
